import styled, { keyframes } from "styled-components";

import { Button } from "../components/button";
import { colors } from "../assets/styles/parameters";
import { useAuth } from "react-oidc-context";

export const Login = () => {
  const auth = useAuth();

  const handleLogin = () => {
    auth.signinRedirect();
  };

  return (
    <PageWrapper>
      {/* Left panel — decorative brand panel */}
      <BrandPanel>
        <BrandInner>
          <LogoMark>O</LogoMark>
          <BrandName>Oasis Home Healthcare</BrandName>
          <BrandTagline>
            Promote, Protect, and Advance Health at Home
          </BrandTagline>
        </BrandInner>
        <BrandFooter>
          &copy; {new Date().getFullYear()} Oasis Home Healthcare LLC &mdash;
          West Chester, OH
        </BrandFooter>
      </BrandPanel>

      {/* Right panel — login card */}
      <FormPanel>
        <LoginCard>
          <CardEyebrow>Admin Portal</CardEyebrow>
          <CardTitle>Sign in to your account</CardTitle>
          <CardSubtitle>
            Secure access for Oasis Healthcare administrators. Click the button
            below to authenticate via your organisation&apos;s identity
            provider.
          </CardSubtitle>

          <Button
            text="Continue with Single Sign-On"
            varient="gold"
            onClick={handleLogin}
          />

          <SecurityNote>
            <LockIcon viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 1C8.676 1 6 3.676 6 7v1H4v14h16V8h-2V7c0-3.324-2.676-6-6-6zm0 2c2.276 0 4 1.724 4 4v1H8V7c0-2.276 1.724-4 4-4zm0 9a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" />
            </LockIcon>
            Protected by OAuth 2.0 / OIDC &mdash; your credentials are never
            stored here.
          </SecurityNote>
        </LoginCard>
      </FormPanel>
    </PageWrapper>
  );
};

export default Login;

/* ─── Animations ─────────────────────────────────────────── */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Layout ─────────────────────────────────────────────── */

const PageWrapper = styled.div`
  display: flex;
  min-height: 100dvh;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

/* ─── Left brand panel ───────────────────────────────────── */

const BrandPanel = styled.aside`
  flex: 0 0 42%;
  /* Deep emerald — primary brand colour from ui_client design system */
  background-color: ${colors.emerald};
  background-image: linear-gradient(
    135deg,
    ${colors.emerald} 0%,
    #0a3d2e 60%,
    ${colors.emerald} 100%
  );
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 3rem 3.5rem;
  color: #ffffff;

  @media (max-width: 768px) {
    flex: none;
    padding: 2.5rem 2rem;
    min-height: 240px;
  }
`;

const BrandInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  animation: ${fadeUp} 0.7s ease both;
`;

const LogoMark = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  /* Gold leaf accent — aligned with ui_client $accent-gold */
  background-color: ${colors.gold};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 1.5rem;
  letter-spacing: -1px;
`;

const BrandName = styled.h1`
  font-size: clamp(1.6rem, 3vw, 2.4rem);
  font-weight: 400;
  letter-spacing: 0.04em;
  line-height: 1.2;
  margin: 0 0 1rem;
  color: #ffffff;
`;

const BrandTagline = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.6;
  max-width: 280px;
  margin: 0;
`;

const BrandFooter = styled.p`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
`;

/* ─── Right form panel ───────────────────────────────────── */

const FormPanel = styled.main`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Parchment background — aligned with ui_client $warm-neutral */
  background-color: ${colors.parchment};
  padding: 3rem 2rem;
`;

const LoginCard = styled.div`
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 12px;
  padding: 3rem 2.5rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  animation: ${fadeUp} 0.6s ease 0.1s both;

  /* Override Button margin from base component */
  button,
  a {
    width: 100%;
    justify-content: center;
    margin-bottom: 0;
  }
`;

const CardEyebrow = styled.p`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${colors.gold};
  margin: 0 0 0.75rem;
`;

const CardTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  color: ${colors.emerald};
  margin: 0 0 0.75rem;
  line-height: 1.2;
`;

const CardSubtitle = styled.p`
  font-size: 0.95rem;
  color: #6c757d;
  line-height: 1.6;
  margin: 0 0 2rem;
`;

const SecurityNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 1.5rem;
  font-size: 0.78rem;
  color: #adb5bd;
  line-height: 1.5;
`;

const LockIcon = styled.svg`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  margin-top: 2px;
  fill: #adb5bd;
`;
